import content from './content';
import API from '@/services/api';
import AvatarPlace from './AvatarPlace';
import { useRef, useState } from 'react';
import Button from '@CS/components/Button';
import { useTranslation } from '@CS/hooks';
import { useNavigateQuery } from '@/hooks';

import {
  DefaultValues,
  getRandomValues
} from '@/components/AvatarOptionsPanel/settings';

import { mapAvatarData, OPTIONS, TABS } from '@/utils/common';
import AvatarOptionsPanel from '@/components/AvatarOptionsPanel';
import type { IAvatarOptions } from '../AvatarOptionsPanel/types';
import { addPlexureTags, removePlexureTags } from '@/services/app/mcd';
import { Control, Container, ControlAside } from './AvatarCreator.styles';
import { ErrorPopup } from '@/components/Popups';
import { getCampaign } from '@/redux/campaign/selectors';
import { connect } from 'react-redux';
import type { CampaignState } from '@/redux/campaign/types';

type AvatarCreatorProps = {
  onTabUpdate: (index: number) => void;
  data?: IAvatarOptions;
  baseModelUrl: string;
  onBack: () => void;
  id: string | null;
  canBack?: boolean;
  members?: number;
  avatars: number;
  campaign: CampaignState;
};

const AvatarCreator = ({
  baseModelUrl,
  onTabUpdate,
  members = 0,
  canBack,
  avatars,
  onBack,
  data,
  id,
  campaign
}: AvatarCreatorProps) => {
  const avatar = useRef(0);
  const saving = useRef(false);
  const navigate = useNavigateQuery();

  const [isConfirm, setIsConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scrollRight, setScrollRight] = useState(false);

  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const { title, confirm, yes, no } = useTranslation(content);
  const [options, setOptions] = useState(data || DefaultValues);
  const [currentTab, setCurrentTab] = useState<string>(OPTIONS.details);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [serverNameError, setServerNameError] = useState<string | null>(null);

  const onSubmit = async (headshot: File) => {
    try {
      if (id) {
        await updateAvatar(id, headshot);
      } else {
        await createAvatar(headshot);
      }
      setSubmitting(false);
    } catch (error: any) {
      handleNameError(error);
      setSubmitting(false);
    }
  };

  const onTabClick = (value: string) => {
    if (value !== '-1') {
      const index = TABS.indexOf(value);
      setCurrentTabIndex(index);
      setCurrentTab(value);
      onTabUpdate(index);
    }
    else {
      setOptions(
        (options) =>
          ({
            ...getRandomValues(),
            name: options.name
          } as IAvatarOptions)
      );
    }
  };

  const handleNameError = (error: any) => {
    const message = error.response?.data?.message || '';
    const isAbusiveWordError = message.toLowerCase().includes('abusive');
    if (isAbusiveWordError) {
      const { abusiveWordError } = campaign.translations;
      setServerNameError(String(abusiveWordError));
      setShowErrorModal(true);
      setCurrentTab(OPTIONS.details);
      setCurrentTabIndex(TABS.indexOf(OPTIONS.details));
      return;
    }
    setServerNameError('There was an error, please try again later');
    setShowErrorModal(true);
  }

  const createAvatar = async (
    headshot: File
  ): Promise<NodeJS.Timeout | void> => {
    try {
      const config = mapAvatarData(options);
      const { createdAvatarId: id } = await API.createAvatar(config);
      const [headshotUrl, headshotImageUrl] = await API.getHeadshotUrl(id);

      await API.uploadHeadshot(headshotUrl, headshot);
      await API.updateAvatar({ id, headshotImageUrl });
      setServerNameError('');
      setShowErrorModal(false);

      let tagId = Math.min(53 + avatars, 60);

      avatars && (await removePlexureTags([`NL_Custom_${tagId}`]));

      tagId = Math.min(tagId + 1, 60);

      addPlexureTags([`NL_Custom_${tagId}`]).catch((error) =>
        console.error(error)
      );

      if (++avatar.current >= members)
        return setTimeout(() => navigate('/overview'), 500);

      setCurrentTab(OPTIONS.details);
      setOptions(DefaultValues);
      setCurrentTabIndex(0);
      setSubmitting(false);
      setIsConfirm(false);
      onTabUpdate(0);
    } catch (error: any) {
      handleNameError(error);
      return;
    }
  };

  const updateAvatar = async (id: string, headshot: File) => {
    try {
      if (saving.current) {
        onTabUpdate(TABS.length);
        setIsConfirm(true);
      }

      const [headshotUrl, headshotImageUrl] = await API.getHeadshotUrl(id);
      await API.uploadHeadshot(headshotUrl, headshot);
      const config = mapAvatarData(options);
      await API.updateAvatar({ id, ...config, headshotImageUrl });
      setServerNameError('');
      setShowErrorModal(false);

      !saving.current && setTimeout(() => navigate('/overview'), 500);
      saving.current && setSubmitting(false);
      saving.current = false;
    } catch (error: any) {
      handleNameError(error);
      return;
    }
  };

  const onDisagree = () => {
    setTimeout(() => onTabClick(currentTab), 20);
    setScrollRight(true);
    setIsConfirm(false);
  };

  const onBackClick = () => {
    const next = currentTabIndex - 1;
    next === 2 && setScrollRight(false);
    canBack && !currentTabIndex ? onBack() : onTabClick(TABS[next]);
  };

  const onNextClick = () => {
    const next = currentTabIndex + 1;
    next === 3 && setScrollRight(true);

    if (currentTabIndex !== TABS.length - 1) {
      return onTabClick(TABS[next]);
    }

    onTabUpdate(TABS.length);
    setIsConfirm(true);
  };

  const takeScreenShot = () => {
    if (!submitting) setSubmitting(true);
  };

  return (
    <Container>
      <AvatarPlace
        color={options.favoriteColor}
        baseModelUrl={baseModelUrl}
        submitting={submitting}
        onSubmit={onSubmit}
        options={options}
        large={isConfirm}
        tab={currentTab}
      />

      <Control>
        {isConfirm ? (
          <>
            <ControlAside>
              <h2>{title}</h2>
              <span>{confirm}</span>
              <Button text={yes} onClick={takeScreenShot} />
              <Button text={no} variant="secondary" onClick={onDisagree} />
            </ControlAside>
          </>
        ) : (
          <AvatarOptionsPanel
            disabledBack={!canBack && !currentTabIndex}
            onBackClick={onBackClick}
            onSaveClick={takeScreenShot}
            onNextClick={onNextClick}
            scrollRight={scrollRight}
            onTabClick={onTabClick}
            setOptions={setOptions}
            activeTab={currentTab}
            options={options}
          />
        )}
      </Control>
      {showErrorModal && (
        <ErrorPopup
          error={String(serverNameError)}
          onClose={() => setShowErrorModal(false)}
          onClick={() => setShowErrorModal(false)}
        />
      )}
    </Container>
  );
};

const mapStateToProps = (state: never) => ({
  campaign: getCampaign(state)
});

export default connect(mapStateToProps)(AvatarCreator);